import type { AxiosError, AxiosResponse } from "axios";
import { axiosClient } from "utilz";
import { apiErrorHandler, debugConsole } from "utilz/helpers";
import type {
  QbItemList,
  QbServiceCategory,
  QbServiceCategoryMapping,
  QbServiceCategoryMappingInput,
} from "shared/types/integrations";

const BASE_URL = import.meta.env.VITE_APP_INTEGRATION_BASE_URL;
const BILLING_BASE_URL = import.meta.env.VITE_APP_BILLING_BASE_URL;

export const connectQb = async () => {
  try {
    const url = `${BASE_URL}/v1/qb/connect?frontendUrl=${window.location.origin}`;
    const response = await axiosClient.get(`${url}`);
    const resData = response.data;
    if (response.status === 301 || response.status === 302) {
      const redirectUrl = response.headers.location;
      debugConsole(redirectUrl);
    }
    if (resData && resData.statusCode === 200) {
      //   window.location.href = resData;
      return resData.data;
    } else {
      throw new Error(resData?.message);
    }
  } catch (error: unknown) {
    const axiosError = error as AxiosError;
    if (axiosError?.response?.status === 301 || axiosError?.response?.status === 302) {
      const redirectUrl = axiosError?.response?.headers?.location;
      debugConsole(redirectUrl);
    }
    throw error;
  }
};

export const callbackToQb = async ({
  state,
  code,
  realmId,
  secret,
  originalUrl,
}: {
  state: string;
  code: string;
  realmId: string;
  secret: string;
  originalUrl: string;
}) => {
  try {
    const url = `${BASE_URL}/v1/qb/callback`;
    const response = await axiosClient.get(`${url}`, {
      params: {
        state,
        code,
        realmId,
        secret,
        // originalUrl,
      },
    });
    const resData = response.data;
    if (response.status === 301 || response.status === 302) {
      const redirectUrl = response.headers.location;
      debugConsole(redirectUrl);
    }
    if (resData && resData.statusCode === 200) {
      //   window.location.href = resData;
      return resData.data;
    } else {
      throw new Error(resData?.message);
    }
  } catch (error: unknown) {
    const axiosError = error as AxiosError;
    if (axiosError?.response?.status === 301 || axiosError?.response?.status === 302) {
      const redirectUrl = axiosError?.response?.headers?.location;
      debugConsole(redirectUrl);
    }
    throw error;
  }
};

export const getQbProducts = async () => {
  try {
    const url = `${BASE_URL}/qb/products`;
    const response = await axiosClient.get(`${url}`);
    const resData = response.data;
    if (resData.success && resData.statusCode === 200) {
      return resData.data;
    }
    throw response;
  } catch (error: unknown) {
    apiErrorHandler(error as AxiosError | AxiosResponse);
  }
};

export const getQbTax = async () => {
  try {
    const url = `${BASE_URL}/qb/tax-rates`;
    const response = await axiosClient.get(`${url}`);
    const resData = response.data;
    if (resData.success && resData.statusCode === 200) {
      return resData.data;
    }
    throw response;
  } catch (error: unknown) {
    apiErrorHandler(error as AxiosError | AxiosResponse);
  }
};

export const sendToQb = async ({
  invoiceId,
  currency,
  qbRealmId,
}: {
  invoiceId: string;
  currency: string;
  qbRealmId: string;
}) => {
  try {
    const url = `${BILLING_BASE_URL}/v1/invoice/send-to-qb`;
    const data = {
      dd_invoice_id: invoiceId,
      currency,
      realm_id: qbRealmId,
    };
    const response = await axiosClient.post(`${url}`, data);
    const resData = response.data;
    if (resData.success && resData.statusCode === 200) {
      return resData.data;
    }
    throw response;
  } catch (error: unknown) {
    apiErrorHandler(error as AxiosError | AxiosResponse);
  }
};

export const bulkSendToQb = async ({
  invoiceIds,
  currency,
  qbRealmId,
}: {
  invoiceIds: string[];
  currency: string;
  qbRealmId: string;
}) => {
  try {
    const url = `${BILLING_BASE_URL}/v1/billing/bulk/send-to-qb`;
    const data = {
      invoice_ids: invoiceIds,
      currency,
      realm_id: qbRealmId,
    };
    const response = await axiosClient.post(`${url}`, data);
    const resData = response.data;
    if (resData.success && resData.statusCode === 200) {
      return resData.data;
    }
    throw response;
  } catch (error: unknown) {
    apiErrorHandler(error as AxiosError | AxiosResponse);
  }
};

export const getQbRealmId = async () => {
  try {
    const url = `${BASE_URL}/qb/realm-id`;
    const response = await axiosClient.get(`${url}`);
    const resData = response.data;
    if (resData.success && resData.statusCode === 200) {
      return resData.data;
    }
    throw response;
  } catch (error: unknown) {
    apiErrorHandler(error as AxiosError | AxiosResponse);
  }
};

export const getQbServiceCategoryMappings = async (): Promise<
  QbServiceCategoryMapping[]
> => {
  try {
    const url = `${BASE_URL}/v1/qb/service-category-mapping`;
    const response = await axiosClient.get(`${url}`);
    const resData = response.data;
    if (resData.success && resData.statusCode === 200) {
      return resData.data ?? [];
    }
    throw response;
  } catch (error: unknown) {
    return apiErrorHandler(error as AxiosError | AxiosResponse);
  }
};

/**
 * Upsert — the same endpoint creates and re-points, keyed on `service_category`.
 * Categories absent from `mappings` are left untouched; clearing one requires
 * `deleteQbServiceCategoryMapping`.
 */
export const mapQbServiceCategories = async (
  mappings: QbServiceCategoryMappingInput[],
) => {
  try {
    const url = `${BASE_URL}/v1/qb/service-category-mapping`;
    const response = await axiosClient.post(`${url}`, mappings);
    const resData = response.data;
    if (resData.success && resData.statusCode === 200) {
      return resData.data;
    }
    throw response;
  } catch (error: unknown) {
    return apiErrorHandler(error as AxiosError | AxiosResponse);
  }
};

/**
 * A deleted category falls back to the legacy default item during invoice sync,
 * so a 404 here means the mapping was already absent — the desired end state.
 */
export const deleteQbServiceCategoryMapping = async (
  category: QbServiceCategory,
) => {
  try {
    const url = `${BASE_URL}/v1/qb/service-category-mapping/${category}`;
    const response = await axiosClient.delete(`${url}`);
    const resData = response.data;
    if (resData.success && resData.statusCode === 200) {
      return resData.data;
    }
    throw response;
  } catch (error: unknown) {
    return apiErrorHandler(error as AxiosError | AxiosResponse);
  }
};

export const getQbItems = async ({
  page = 1,
  perPage = 500,
  search,
}: {
  page?: number;
  perPage?: number;
  search?: string;
} = {}): Promise<QbItemList | undefined> => {
  try {
    const url = `${BASE_URL}/qb/products`;
    const response = await axiosClient.get(`${url}`, {
      params: { page, perPage, search },
    });
    const resData = response.data;
    if (resData.success && resData.statusCode === 200) {
      return resData.data;
    }
    throw response;
  } catch (error: unknown) {
    return apiErrorHandler(error as AxiosError | AxiosResponse);
  }
};
